function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) { try { var info = gen[key](arg); var value = info.value; } catch (error) { reject(error); return; } if (info.done) { resolve(value); } else { Promise.resolve(value).then(_next, _throw); } }

function _asyncToGenerator(fn) { return function () { var self = this, args = arguments; return new Promise(function (resolve, reject) { var gen = fn.apply(self, args); function _next(value) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value); } function _throw(err) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err); } _next(undefined); }); }; }

import normaliseDagNode from './normalise-dag-node';
import Cid from 'cids';
/**
 * Walk an IPLD path to find all the nodes and path boundaries it traverses.
 *
 * Normalizes nodes into a common structure:
 *
 * ```js
 * { cid: String, type: 'dag-cbor' | 'dag-pb' | 'git-raw' | ..., data: *, links: [{cid, name}] }
 * ```
 *
 * Path boundaries capture the source and target cid where a path traverses a link:
 *
 * ```js
 * { linkPath: 'a/b', source: `zdpHash1` target: `zdpHash2`' }
 * ```
 *
 * Usage:
 * ```js
 * const res = resolveIpldPath(getIpfs, 'zdpuHash' '/favourites/0/a/css')
 * const {targetNode, canonicalPath, localPath, nodes, pathBoundaries} = res
 * ```
 * Where:
 * - `targetNode` is the normalised node that the path lands in.
 * - `canonicalPath` is the shortest cid + path that can be used to locate the targetNode
 * - `localPath` is the tail part of the path that is local to the targetNode. May be ''
 * - `nodes` is the array of nodes that the path traverses.
 * - `pathBoundaries` is the array of links that the path traverses.
 *
 * @param {function()} ipldGet fn that returns a promise of the ipld data for a (cid, path, options)
 * @param {string} sourceCid the root hash
 * @param {string} path everything after the hash
 * @param {Object[]} nodes accumulated node info
 * @param {Object[]} pathBoundaries accumulated path boundary info
 * @returns {{targetNode: Object, canonicalPath: String, localPath: String, nodes: Object[], pathBoundaries: Object[]}} resolved path info
 */

export default function resolveIpldPath(_x, _x2, _x3) {
  return _resolveIpldPath.apply(this, arguments);
}

function _resolveIpldPath() {
  _resolveIpldPath = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee(ipld, sourceCid, path) {
    var nodes,
        pathBoundaries,
        _yield$ipldGetNodeAnd,
        value,
        remainderPath,
        sourceCidStr,
        node,
        linkPath,
        link,
        canonicalPath,
        targetNode,
        _args = arguments;

    return regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            nodes = _args.length > 3 && _args[3] !== undefined ? _args[3] : [];
            pathBoundaries = _args.length > 4 && _args[4] !== undefined ? _args[4] : [];
            _context.next = 4;
            return ipldGetNodeAndRemainder(ipld, sourceCid, path);

          case 4:
            _yield$ipldGetNodeAnd = _context.sent;
            value = _yield$ipldGetNodeAnd.value;
            remainderPath = _yield$ipldGetNodeAnd.remainderPath;
            sourceCidStr = sourceCid.toString();
            node = normaliseDagNode(value, sourceCidStr);
            nodes.push(node);
            linkPath = findLinkPath(path, remainderPath);
            link = findLink(node, linkPath);

            if (!link) {
              _context.next = 15;
              break;
            }

            pathBoundaries.push(link); // Go again, using the link.target as the sourceCid, and the remainderPath as the path.

            return _context.abrupt("return", resolveIpldPath(ipld, new Cid(link.target), remainderPath, nodes, pathBoundaries));

          case 15:
            // we made it to the containing node. Hand back the info
            canonicalPath = path ? "".concat(sourceCidStr).concat(path) : sourceCidStr;
            targetNode = node;
            return _context.abrupt("return", {
              targetNode: targetNode,
              canonicalPath: canonicalPath,
              localPath: path,
              nodes: nodes,
              pathBoundaries: pathBoundaries
            });

          case 18:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));
  return _resolveIpldPath.apply(this, arguments);
}

export function ipldGetNodeAndRemainder(_x4, _x5, _x6) {
  return _ipldGetNodeAndRemainder.apply(this, arguments);
}
/**
 * Find the link object that matches linkPath
 *
 * @param {Object} node a `normalisedDagNode`
 * @param {Object} linkPath an IPLD path string
 * @returns {Object} the link object for `linkPath`
 */

function _ipldGetNodeAndRemainder() {
  _ipldGetNodeAndRemainder = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee2(ipld, sourceCid, path) {
    return regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            _context2.next = 2;
            return ipld.get(sourceCid);

          case 2:
            _context2.t0 = _context2.sent;
            _context2.next = 5;
            return ipld.resolve(sourceCid, path || '/').first();

          case 5:
            _context2.t1 = _context2.sent.remainderPath;
            return _context2.abrupt("return", {
              value: _context2.t0,
              remainderPath: _context2.t1
            });

          case 7:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  }));
  return _ipldGetNodeAndRemainder.apply(this, arguments);
}

export function findLink(node, linkPath) {
  if (!linkPath) return null;
  if (!node) return null;
  var links = node.links;
  var link = links.find(function (link) {
    return link.path === linkPath;
  });
  return link;
}
export function findLinkPath(fullPath, remainderPath) {
  if (!fullPath || fullPath === '/') return null;
  if (!remainderPath) return trimSlashes(fullPath);

  if (!fullPath.endsWith(remainderPath)) {
    throw new Error('Requested IPLD path should end with the remainder path', {
      fullPath: fullPath,
      remainderPath: remainderPath
    });
  } // Remove remainder path from end of full path to get link path


  var linkPath = fullPath.substring(0, fullPath.length - remainderPath.length);
  return trimSlashes(linkPath);
}
export function trimSlashes(str) {
  if (str.startsWith('/')) {
    str = str.substring(1);
  }

  if (str.endsWith('/')) {
    str = str.substring(0, str.length - 1);
  }

  return str;
}